/**
 * Formats account holder names from a managed account into a comma-separated string.
 *
 * For individual accounts, returns a single name (e.g., "John Doe")
 * For joint accounts, returns comma-separated names (e.g., "John Doe, Jane Doe")
 * For trusts, returns the trust's display name
 *
 * @param managedAccount - The managed account object containing account holders
 * @returns Formatted string of account holder name(s), or empty string if no holders
 */

/**
 * Plain TypeScript interfaces representing the minimal shape needed
 * for account holder formatting. These are intentionally NOT tied to
 * GraphQL schema types to keep this utility function decoupled and testable.
 *
 * GraphQL types (Client, Trust) are structurally compatible with these
 * interfaces and will work seamlessly.
 */

interface ClientAccountHolder {
  __typename: 'Client';
  commonFullName: string;
}

interface TrustAccountHolder {
  __typename: 'Trust';
  name: string;
}

type AccountHolder = ClientAccountHolder | TrustAccountHolder;

interface ManagedAccountWithHolders {
  accountHolders: AccountHolder[];
}

export function getAccountHolderNames(managedAccount: ManagedAccountWithHolders): string {
  const { accountHolders } = managedAccount;

  return accountHolders
    .map(holder => {
      const typename = holder.__typename;
      let name: string;

      if (isValidClient(holder)) {
        name = holder.commonFullName;
      } else if (isValidTrust(holder)) {
        name = holder.name;
      } else {
        throw new Error(`Unknown account holder type: ${typename}`);
      }

      if (!name || name.trim() === '') {
        throw new Error(`Account holder name cannot be empty for type: ${typename}`);
      }

      return name;
    })
    .join(', ');
}

function isValidClient(holder: AccountHolder): holder is ClientAccountHolder {
  return holder.__typename === 'Client';
}

function isValidTrust(holder: AccountHolder): holder is TrustAccountHolder {
  return holder.__typename === 'Trust';
}
